// Angular and RJX Imports
// =========================================================
import { Injectable } from '@angular/core'
// AWS Imports
// =========================================================
import { type GraphQLSubscription } from '@aws-amplify/api'
import { API, graphqlOperation } from 'aws-amplify'
// Prime NG Imports
// =========================================================
import { DialogService } from 'primeng/dynamicdialog'
// Axios Imports
// =========================================================
import axios from 'axios'
// Custom Imports
// =========================================================
import { ToastAlertComponent } from 'src/app/03_shared-components/01_alerts/toast-alert/toast-alert.component'
import { ExecuteDeleteAPI, ExecuteGetAPI, ExecutePutAPI, ExecutePostAPI } from './execute_api-calls'
import {
  type DataStoreIndexJobsInterface,
  type DeleteStoreIndexJobsInterface,
  type ApprovalStoreIndexJobsInterface,
  type CopyStoreIndexJobInterface,
  type DataStoreIndexIndexesInterface
} from 'src/app/core/interfaces/data-expected-from-backend/store-index-interfaces'
import { type ResponseSubscription } from '../../../API'
import * as subscriptions from '../../../graphql/subscriptions'
import { FormatKey } from 'src/app/utils/global_functions'
import { type StoreIndexCreateEditJobInterface } from '../interfaces/data-expected-from-backend/store-index-create-edit-job-interface'
import { TriggerApiCallsService } from '../services/cancel-api-call'

@Injectable()
export class StoreIndexAPIs {
  clearApiCalls: boolean = false

  constructor (
    public dialogService: DialogService,
    public toastAlert: ToastAlertComponent,
    public triggerApiCallsService: TriggerApiCallsService
  ) {
    // ApiCall Service
    this.triggerApiCallsService.triggerApiCalls$.subscribe({
      next: data => {
        this.clearApiCalls = data['clear_api_calls']
      }
    })
  }

  async reArrangeDetailData (rowData) {
    const data = []
    await rowData.forEach(item => {
      // Find index of the null value object
      const nullIndex = item.detail_data.findIndex(obj => obj.attribute_value === null)

      // If found and not already at front
      if (nullIndex > 0) {
        const nullObj = item.detail_data.splice(nullIndex, 1)[0] // Remove it
        item.detail_data.unshift(nullObj) // Put it at front
      }
      data.push(item)
    })
    return data
  }

  // Get a list of all stored indexes
  async GetAllStoreIndexes () {
    // the api call path with the parameter string is needed
    const path = '/store_index/grid'
    const messages = {
      success: '---> API Call Successful: Retrieved All Store Indexes',
      error: 'Error retrieving rowData for the Store Index Execution and Management Grid.'
    }

    const response = await ExecuteGetAPI(path, this, messages)
    if (response && response.is_success) {
      return await axios
        .get(response.data, {})
        .then(res => {
          console.log('res: ', res)
          // Puts the "All" attribute on top
          res.data.forEach(item => {
            const nullIndex = item.detail_data.findIndex(obj => obj.attribute_value === null)
            if (nullIndex > 0) {
              const nullObj = item.detail_data.splice(nullIndex, 1)[0]
              item.detail_data.unshift(nullObj)
            }
          })
          return res.data
        })
        .catch(error => {
          console.error('Error retrieving store index data: ', error)
          this.toastAlert.showError('Error Loading Data')
          throw error
        })
    } else {
      return []
    }
  }

  // On approve store index
  async OnApproveJobs (indexIds: ApprovalStoreIndexJobsInterface) {
    // the api call path with the parameter string is needed
    const path = '/store_index/status/approve'
    const totalNumOfJobs = indexIds.index_ids.length
    const suffix = totalNumOfJobs === 1 ? '' : 's'

    const messages = {
      success: `Successfully approved ${totalNumOfJobs} store index job${suffix}.`,
      error: `Error approving ${totalNumOfJobs} store index job${suffix}.`,
      showSuccessToast: true
    }

    return await ExecutePutAPI(path, this, indexIds, messages)
  }

  // On unapprove store index
  async OnUnapproveJobs (indexIds: ApprovalStoreIndexJobsInterface) {
    console.log('indexIds', indexIds)
    // the api call path with the parameter string is needed
    const path = '/store_index/status/unapprove'
    const totalNumOfJobs = indexIds.index_ids.length
    const suffix = totalNumOfJobs === 1 ? '' : 's'
    const messages = {
      success: `Successfully unapproved ${totalNumOfJobs} store index job${suffix}.`,
      error: `Error unapproving ${totalNumOfJobs} store index job${suffix}.`,
      showSuccessToast: true
    }

    return await ExecutePutAPI(path, this, indexIds, messages)
  }

  // On run jobs
  async OnRunJobs (jobNames: DataStoreIndexJobsInterface) {
    // the api call path with the parameter string is needed
    const path = '/jobs/qsi'
    const totalNumOfJobs = jobNames.job_names.length
    const suffix = totalNumOfJobs === 1 ? '' : 's'

    const messages = {
      success: `Successfully ran ${totalNumOfJobs} store index job${suffix}.`,
      error: `Error running ${totalNumOfJobs} store index job${suffix}.`,
      showSuccessToast: true
    }

    return await ExecutePostAPI(path, this, jobNames, messages)
  }

  // Delete store index
  async OnDeleteJobs (jobsToDelete: DeleteStoreIndexJobsInterface) {
    console.log('jobsToDelete', jobsToDelete)
    // the api call path with the parameter string is needed
    const path = '/store_index/jobs/delete_store_index_jobs'
    const totalNumOfJobs = jobsToDelete.store_index_job_names.length
    const suffix = totalNumOfJobs === 1 ? '' : 's'

    const messages = {
      success: `Successfully deleted ${totalNumOfJobs} store index job${suffix}.`,
      error: `Error deleting ${totalNumOfJobs} store index job${suffix}.`,
      showSuccessToast: true
    }
    return await ExecuteDeleteAPI(path, this, messages, jobsToDelete)
  }

  // Delete store index_ids
  async OnDeleteIndexes (indexesObj: DataStoreIndexIndexesInterface) {
    // the api call path with the parameter string is needed
    const path = '/store_index/jobs/delete_store_index_job_results'

    const totalNumOfIndexes = indexesObj.index_ids.length || 0
    const suffix = totalNumOfIndexes === 1 ? '' : 'es'

    const messages = {
      success: `Successfully deleted ${totalNumOfIndexes} store index${suffix}.`,
      error: `Error deleting ${totalNumOfIndexes} store index${suffix}.`,
      showSuccessToast: true
    }
    console.log('indexesObj', indexesObj)
    return await ExecuteDeleteAPI(path, this, messages, indexesObj)
  }

  async CreateStoreIndexJob (data: StoreIndexCreateEditJobInterface) {
    const path = '/store_index/jobs/create'
    const messages = {
      success: `Successfully created job: ${data.job_name}.`,
      error: `Error creating job: ${data.job_name}.`,
      showSuccessToast: true
    }

    return await ExecutePostAPI(path, this, data, messages)
  }

  async SaveStoreIndexJob (data: StoreIndexCreateEditJobInterface) {
    const path = '/store_index/jobs/edit'
    const messages = {
      success: `Successfully saved job: ${data.job_name}.`,
      error: `Error saving job: ${data.job_name}.`,
      showSuccessToast: true
    }

    return await ExecutePutAPI(path, this, data, messages)
  }

  async CopyStoreIndexJob (data: CopyStoreIndexJobInterface) {
    const path = '/store_index/jobs/copy'
    const totalNumOfJobs = data.jobs_to_copy.length
    const suffix = totalNumOfJobs === 1 ? '' : 's'

    const messages = {
      success: `Successfully copied ${totalNumOfJobs} store index job${suffix}.`,
      error: `Error copying ${totalNumOfJobs} store index job${suffix}.`,
      showSuccessToast: true
    }

    return await ExecutePostAPI(path, this, data, messages)
  }

  async GetProductFamilyAttributes (product_id: string) {
    const path = `/store_index/jobs/product_attributes/${product_id}`

    const messages = {
      success: `Successfully retrieved Family attributes for product: ${product_id}.`,
      error: `Error retrieving Family attributes for product: ${product_id}.`,
      showSuccessToast: false
    }

    return await ExecuteGetAPI(path, this, messages)
  }

  async JobGridDataSubscription (gridApi) {
    console.log('inside JobGridDataSubscription')

    const subscription = API.graphql<GraphQLSubscription<ResponseSubscription>>(
      graphqlOperation(subscriptions.response, {})
    ).subscribe({
      next: ({ provider, value }) => {
        if (!gridApi || gridApi['destroyCalled']) return

        const response = value.data?.response
        if (!response || !response.job_name || !response.status) return

        const rawStatus = response.status

        let parsedStatuses: any[] = []
        if (typeof rawStatus === 'string' && rawStatus.trim().startsWith('[')) {
          try {
            const safeJson = rawStatus.replace(/'/g, '"').replace(/\bNone\b/g, 'null')

            parsedStatuses = JSON.parse(safeJson)
          } catch (e) {
            console.warn('Failed to parse status array:', e)
            parsedStatuses = null
          }
        } else {
          parsedStatuses = null
        }

        gridApi.forEachNode(rowNode => {
          if (rowNode.data.job_name === response.job_name) {
            const updatedDetailData = rowNode.data.detail_data.map((detailRow, index) => {
              if (detailRow.job_name === response.job_name) {
                const newStatus = parsedStatuses
                  ? parsedStatuses[index]?.status ?? detailRow.status
                  : rawStatus

                return {
                  ...detailRow,
                  status: newStatus
                }
              }
              return detailRow
            })

            const statuses = updatedDetailData.map(row => row.status)

            gridApi.applyTransactionAsync({
              update: [
                {
                  ...rowNode.data,
                  statuses,
                  detail_data: updatedDetailData
                }
              ]
            })
          }
        })
      },
      error: error => console.warn(error)
    })
  }
}
